import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '../../../textField';
import { isEmail } from 'validator';
import { useNotificationContext } from '@/contexts/notificationContext';
import ErrorMessage from '../../../errorMessage';
import { APP_PERMISSIONS } from './permissions';

type Props = { entity: any; existingUsers: any[]; open: boolean; onCancel: () => void; onSubmit: () => void };

const AddAccessControlDialog = ({ entity, existingUsers = [], open, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const apiContext = useApiContext();
  const { toast } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'owner'>('member');
  const [permissions, setPermissions] = useState<string[]>([]);

  const [error, setError] = useState<any>();
  const [errors, setErrors] = useState<any>({});

  const canGrantOwner = user?.roles?.includes('administrator') || entity?.userAccess?.role === 'owner';

  const permissionGroups: any = APP_PERMISSIONS[entity?.entityType] || {};

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('member');
      setError(null);
      setErrors({});
      const defaultPermissions = [];
      for (const g in permissionGroups) {
        for (const p of permissionGroups[g]) {
          if (p.default) {
            defaultPermissions.push(p.fqn);
          }
        }
      }
      setPermissions(defaultPermissions);
    }
  }, [open, permissionGroups]);

  const updatePermission = (permission: string, selected: boolean) => {
    if (selected) {
      setPermissions((preValues: any) => {
        return [...preValues, permission];
      });
    } else {
      setPermissions((preValues: any) => {
        return preValues.filter((p: string) => p !== permission);
      });
    }
  };

  const addUser = async () => {
    setError(null);
    const errors = { email: '', permissions: '' };
    let hasError = false;
    if (!email) {
      errors['email'] = 'Required';
      hasError = true;
    }
    if (!isEmail(email)) {
      errors['email'] = 'Please enter valid email';
      hasError = true;
    }
    if (user?.email && user.email.toLowerCase() === email.toLowerCase()) {
      errors['email'] = 'You cannot add yourself';
      hasError = true;
    }
    const existing = existingUsers.find((u) => u.email?.toLowerCase() === email.toLowerCase());
    if (existing) {
      errors['email'] = `User with ${email} already present`;
      hasError = true;
    }

    if (role === 'member' && permissions.length === 0) {
      errors['permissions'] = 'Please select at least one permission';
      hasError = true;
    }
    setErrors(errors);
    if (!hasError) {
      try {
        setLoading(true);
        const userAccess = role === 'owner' ? { role: 'owner' } : { permissions };
        await apiContext.createEntityUser(entity.entityType, entity.entityDefId, entity.id, email, userAccess);
        toast({ message: t('entity.administration.accessControl.addUserDialog.saved') });
        onSubmit();
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>{t('entity.administration.accessControl.addUserDialog.header')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {error && (
            <Grid size={12}>
              <ErrorMessage error={error} />
            </Grid>
          )}

          {entity?.entityType === 'pipelineInstance' && (
            <Grid size={12}>
              <Alert severity="info" sx={{ fontSize: '0.8rem', borderRadius: '8px' }}>
                {t(
                  'entity.administration.accessControl.addUserDialog.pipelineCascadeNotice',
                  'When added to this pipeline, this user will automatically be granted access to all data sources, agents, and apps in the pipeline.',
                )}
              </Alert>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              label={t('entity.administration.accessControl.addUserDialog.fields.email')}
              value={email}
              type="email"
              required
              autoFocus
              error={errors['email']}
              onChange={setEmail}
              maxLength={100}
            />
          </Grid>

          {canGrantOwner && (
            <Grid size={12}>
              <FormControl component="fieldset" size="small">
                <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>
                  {t('entity.administration.accessControl.addUserDialog.role', 'Role')}
                </FormLabel>
                <RadioGroup row value={role} onChange={(e) => setRole(e.target.value as 'member' | 'owner')}>
                  <FormControlLabel
                    value="member"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {t('entity.administration.accessControl.addUserDialog.roles.member', 'Member (Custom permissions)')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="owner"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {t('entity.administration.accessControl.addUserDialog.roles.owner', 'Owner (Full access)')}
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          )}

          {role === 'owner' ? (
            <Grid size={12}>
              <Alert severity="success" sx={{ fontSize: '0.8rem', borderRadius: '8px' }}>
                {t(
                  'entity.administration.accessControl.addUserDialog.ownerNotice',
                  'Owners hold full administrative permissions to view, configure, and manage this resource.',
                )}
              </Alert>
            </Grid>
          ) : (
            <>
              <Grid size={12}>
                <Divider sx={{ my: 1 }}>{t('entity.administration.accessControl.permissions.header')}</Divider>
                {errors['permissions'] && <FormHelperText error>{errors['permissions']}</FormHelperText>}
              </Grid>
              {Object.keys(permissionGroups).map((group: string) => {
                return (
                  <Grid key={group} size={12}>
                    <FormControl size="small" component="fieldset">
                      <FormLabel component="legend">
                        {t(`entity.administration.accessControl.permissions.groups.${group}`)}
                      </FormLabel>
                      <FormGroup row>
                        {Object.values(permissionGroups[group]).map((p: any) => {
                          return (
                            <FormControlLabel
                              key={p.fqn}
                              value={p.fqn}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={permissions.includes(p.fqn)}
                                  onChange={(event) => {
                                    updatePermission(p.fqn, event.target.checked);
                                  }}
                                />
                              }
                              label={t(`entity.administration.accessControl.permissions.${p.name}`)}
                            />
                          );
                        })}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                );
              })}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" variant="text" onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {t('common.buttons.cancel')}
        </Button>
        <Button
          loading={loading}
          size="small"
          color="secondary"
          variant="contained"
          onClick={addUser}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          {t('common.buttons.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAccessControlDialog;

